import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { readableOn } from 'src/util/readableColour';
import { PEDIGREE } from 'src/components/Constants';
import { TWIN_TYPE } from '../../pedigree-tool/pedigree/config';
import {
  joinTwinSet,
  unlinkTwinMembers,
  twinCandidatesFor,
  twinMergeCandidatesFor,
  twinGroupOf,
  twinSetIsComplete,
  twinPairKey,
  TWIN_TYPE_LABELS
} from '../../pedigree-tool/pedigree/twinLinkage';
import {
  MULTIPLE_BIRTH_HEADER,
  multipleBirthStatusLine,
  notAMultipleLabel,
  remainderConsequence
} from '../../pedigree-tool/pedigree/multipleBirth';
import {
  recordCardTint,
  recordCardTintBorder
} from './clinical-notes-section/recordCardStyles';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

/**
 * The selected person's place in a multiple birth, and the only way to correct it.
 *
 * TGCLOUD-1643 built this for TWINS. TGCLOUD-1692 made it able to say what it was
 * already able to store: a triplet was reachable — the save path has always sized the
 * set from the number of people in it — but nothing in the product said "triplet". A
 * three-member set read "Identical twin of UPN 4, UPN 5", the card was headed TWIN
 * STATUS, and the button said "Not a twin".
 *
 * The bigger change is that ZYGOSITY IS PER PAIR. A set of three or more does not have
 * one zygosity, and the commonest triplet in a clinic is an identical pair alongside a
 * third who is not. So this card edits the SELECTED PERSON'S OWN pairs, one row each:
 * that keeps the model comprehensible (a card describes its own person) and it means the
 * pair between two OTHER members is edited by selecting one of them, rather than by this
 * card guessing.
 *
 * A pair still reads and behaves exactly as it did — one row, one zygosity — because
 * that is what every twin already on every chart is.
 *
 * Showing the status AT REST remains half the point: a wrong record is only findable if
 * the clinician can see what is recorded without interpreting the diagonals.
 */

/**
 * "UPN 14 - John Smith", or "UPN 14" when there is no name.
 *
 * The UPN shows even when a name is present: it is the identifier a clinician
 * cross-references against, and on a pedigree where several relatives share a surname
 * the name alone does not identify anyone.
 */
const memberLabel = (m) => {
  const upn = m?.upn ?? m?.pid;
  const name = [m?.firstName, m?.lastName].filter(Boolean).join(' ');
  return name
    ? translate('UPN {{upn}} - {{name}}', { upn, name })
    : translate('UPN {{upn}}', { upn });
};

/** The zygosity choices, in the order the context menu offers them. */
const ZYGOSITY_OPTIONS = [
  TWIN_TYPE.monozygotic,
  TWIN_TYPE.dizygotic,
  TWIN_TYPE.unknown
];

/**
 * A sentinel wrapping a pid, so the status SENTENCE can be built once — by
 * `multipleBirthStatusLine`, the same function the vocabulary tests pin — and the names
 * inside it still rendered as clickable links.
 *
 * The alternative was a second renderer composing the same prose out of its parts, which
 * is exactly the wording drift this ticket removed everywhere else.
 *
 * NUL, and it has to be something that cannot occur in prose. The first cut used a plain
 * SPACE, which the sentence for a set of seven or more promptly broke: that wording is
 * "One of a multiple birth of 7 - ...", so a space-digit-space run matched the split and
 * the SET SIZE was rendered as a link to person 7. It also left doubled spaces around
 * every name.
 */
const TOKEN = '\u0000';
const token = (pid) => `${TOKEN}${pid}${TOKEN}`;

export default function MultipleBirthStatus({
  member,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  isDisabled = false,
  onSelectMember
}) {
  const t = useAutoTranslation();

  // The deployment's configured BUTTON colour (Dashboard maps theme.buttonStyle onto
  // palette.secondary). Reserved hues are the reason: the first pass used an indigo that
  // reads as a Genotype record, and the phenotype teal that replaced it made this card
  // compete with actual phenotype records. The button colour belongs to the deployment
  // rather than to any record category, so it marks the card as "part of this app"
  // without claiming to be clinical data of a particular kind.
  const muiTheme = useTheme();
  const ACCENT = muiTheme.palette.secondary?.main || '#2ba9bf';
  const ACCENT_LABEL = React.useMemo(() => readableOn(ACCENT), [ACCENT]);

  const [editing, setEditing] = React.useState(false);
  const [picked, setPicked] = React.useState([]);
  /** pid -> zygosity for THAT pair with the selected person. */
  const [pairTypes, setPairTypes] = React.useState({});

  const self = React.useMemo(
    () => (memberRecords || []).find((m) => m.pid === member?.pid) || member,
    [memberRecords, member]
  );

  const group = React.useMemo(
    () => twinGroupOf(self, memberRecords),
    [self, memberRecords]
  );
  const candidates = React.useMemo(
    () => twinCandidatesFor(self, memberRecords),
    [self, memberRecords]
  );
  // Full siblings already in a DIFFERENT set. Offered separately and behind an explicit
  // confirmation: before TGCLOUD-1692 they were silently absent, so two recorded pairs
  // among four full siblings could never be corrected to quadruplets.
  const mergeCandidates = React.useMemo(
    () => twinMergeCandidatesFor(self, memberRecords),
    [self, memberRecords]
  );

  const partners = group.partners;
  const isMultiple = partners.length > 0;
  const setSize = partners.length + 1;

  /** The zygosity a NEW member defaults to: whatever the set already uses, else unknown. */
  const defaultType = group.twinType || TWIN_TYPE.unknown;

  const initialPairTypes = React.useCallback(() => {
    const next = {};
    partners.forEach((p) => {
      const held = (self?.twinRelations || []).find(
        (r) => String(r?.withPid) === String(p.pid)
      );
      next[p.pid] = held?.type || defaultType;
    });
    [...candidates, ...mergeCandidates].forEach((c) => {
      next[c.pid] = defaultType;
    });
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, candidates, mergeCandidates, self, defaultType]);

  // Reset every transient thing when the selection moves, or a pinned choice would carry
  // to the next person.
  React.useEffect(() => {
    setEditing(false);
    setPicked([]);
    setPairTypes(initialPairTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.pid]);

  // A person with no full siblings on the chart has nothing to share a birth WITH, and no
  // status to show. Render nothing rather than an empty control — the caller drops this in
  // unconditionally.
  if (!self || (!isMultiple && !candidates.length && !mergeCandidates.length)) {
    return null;
  }

  const markSaving = () =>
    setPedigreeState?.((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

  const zygosityChanged = partners.some((p) => {
    const held = (self.twinRelations || []).find(
      (r) => String(r?.withPid) === String(p.pid)
    );
    return (pairTypes[p.pid] || defaultType) !== held?.type;
  });
  // A set whose pairs are not all mutually recorded cannot draw correctly, and before
  // TGCLOUD-1692 there was no way to ask for it to be repaired: Save was gated on a
  // change, and the chained sets the old context menu produced offered no candidates
  // either. Saving re-closes the graph.
  const incomplete = isMultiple && !twinSetIsComplete(self, memberRecords);
  const canSave = picked.length > 0 || zygosityChanged || incomplete;

  const save = () => {
    if (!canSave) return;
    // Every pair this card is responsible for: the selected person's own.
    const overrides = new Map();
    [...partners.map((p) => p.pid), ...picked].forEach((pid) => {
      overrides.set(twinPairKey(self.pid, pid), pairTypes[pid] || defaultType);
    });
    // The fallback for pairs this card does NOT own — those between two other members of
    // the set. Where every row agrees, the set is uniform and that agreement is the
    // honest answer; where they do not, a pair nobody spoke about is `unknown` rather
    // than a guess. `joinTwinSet` preserves anything already recorded regardless, and
    // fills a gap inside an identical component as identical.
    const chosen = [...overrides.values()];
    const uniform =
      chosen.length && chosen.every((choice) => choice === chosen[0])
        ? chosen[0]
        : TWIN_TYPE.unknown;

    setMemberRecords((prev) =>
      joinTwinSet(prev, self.pid, picked, uniform, overrides)
    );
    markSaving();
    setEditing(false);
    setPicked([]);
  };

  const clear = () => {
    setMemberRecords((prev) => unlinkTwinMembers(prev, [self.pid]));
    markSaving();
    setEditing(false);
    setPicked([]);
  };

  const labelFor = (pid) =>
    memberLabel((memberRecords || []).find((m) => m.pid === pid));

  // Grouped by zygosity, in the order the options are offered, so the sentence reads
  // consistently rather than in twinRelations order.
  const statusGroups = ZYGOSITY_OPTIONS.map((type) => ({
    type,
    labels: partners
      .filter((p) => {
        const held = (self.twinRelations || []).find(
          (r) => String(r?.withPid) === String(p.pid)
        );
        return held?.type === type;
      })
      .map((p) => token(p.pid))
  })).filter((g) => g.labels.length);

  // The builders take this card's `t` (TGCLOUD-1893). The tokens are values in
  // the sentence, never part of its key, so they survive translation intact.
  const sentence = multipleBirthStatusLine(
    {
      size: setSize,
      groups: statusGroups
    },
    t
  );

  /** The sentence, with each token rendered as a link to that person. */
  const renderSentence = () => {
    if (!sentence) return notAMultipleLabel(2, t);
    const parts = sentence.split(new RegExp(`${TOKEN}(\\d+)${TOKEN}`));
    return parts.map((part, i) => {
      // Odd indexes are the captured pids.
      if (i % 2 === 0) return <React.Fragment key={i}>{part}</React.Fragment>;
      const pid = Number(part);
      const text = labelFor(pid);
      if (!onSelectMember)
        return <React.Fragment key={i}>{text}</React.Fragment>;
      return (
        <Box
          key={i}
          component="span"
          role="button"
          tabIndex={0}
          onClick={() => onSelectMember(pid)}
          onKeyDown={(e) => e.key === 'Enter' && onSelectMember(pid)}
          sx={{
            color: ACCENT,
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          {text}
        </Box>
      );
    });
  };

  const togglePick = (pid) =>
    setPicked((prev) =>
      prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]
    );

  const zygositySelect = (pid, ariaLabel) => (
    <Select
      size="small"
      value={pairTypes[pid] || defaultType}
      onChange={(e) =>
        setPairTypes((prev) => ({ ...prev, [pid]: e.target.value }))
      }
      inputProps={{ 'aria-label': ariaLabel }}
      sx={{ ml: 'auto', minWidth: 168 }}
    >
      {ZYGOSITY_OPTIONS.map((option) => (
        <MenuItem key={option} value={option}>
          {t(TWIN_TYPE_LABELS[option])}
        </MenuItem>
      ))}
    </Select>
  );

  const row = (key, child) => (
    <Box
      key={key}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
        flexWrap: 'wrap'
      }}
    >
      {child}
    </Box>
  );

  return (
    <Box
      data-testid="twin-status"
      sx={{
        border: `1px solid ${recordCardTintBorder(ACCENT)}`,
        borderLeft: `3px solid ${ACCENT}`,
        backgroundColor: recordCardTint(ACCENT),
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 2
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: ACCENT_LABEL,
          lineHeight: 1
        }}
      >
        {t(MULTIPLE_BIRTH_HEADER)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mt: 0.5
        }}
      >
        <Typography variant="body2">{renderSentence()}</Typography>
        {!editing && (
          <Button
            size="small"
            disabled={isDisabled}
            onClick={() => {
              setPairTypes(initialPairTypes());
              setEditing(true);
            }}
          >
            {isMultiple ? t('Edit') : t('Set')}
          </Button>
        )}
      </Box>

      {editing && (
        <Box sx={{ mt: 1 }}>
          {/* Existing pairs — editable one at a time, because a set of three or more
              does not have a single zygosity. */}
          {partners.map((p) =>
            row(
              `partner-${p.pid}`,
              <>
                <Typography variant="body2">{memberLabel(p)}</Typography>
                {zygositySelect(
                  p.pid,
                  t('Zygosity with {{member}}', { member: memberLabel(p) })
                )}
              </>
            )
          )}

          {candidates.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  mb: 0.5,
                  mt: partners.length ? 1 : 0
                }}
              >
                {t('Full siblings only — a half-sibling cannot share a birth.')}
              </Typography>
              {candidates.map((c) =>
                row(
                  `candidate-${c.pid}`,
                  <>
                    <FormControlLabel
                      // The label sits BESIDE the box, not under it. Typography renders
                      // a <p>, and a block element inside a FormControlLabel pushes
                      // itself onto its own line.
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: -0.5,
                        mr: 0
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={picked.includes(c.pid)}
                          onChange={() => togglePick(c.pid)}
                        />
                      }
                      label={
                        <Typography component="span" variant="body2">
                          {memberLabel(c)}
                        </Typography>
                      }
                    />
                    {zygositySelect(
                      c.pid,
                      t('Zygosity with {{member}}', { member: memberLabel(c) })
                    )}
                  </>
                )
              )}
            </>
          )}

          {/* Somebody already in ANOTHER set. Adding them MERGES the two, which is worth
              saying out loud rather than doing quietly. */}
          {mergeCandidates.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', mt: 1 }}
              >
                {t(
                  'Already recorded in another multiple birth — adding one merges both into a single set.'
                )}
              </Typography>
              {mergeCandidates.map((c) =>
                row(
                  `candidate-${c.pid}`,
                  <>
                    <FormControlLabel
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: -0.5,
                        mr: 0
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={picked.includes(c.pid)}
                          onChange={() => togglePick(c.pid)}
                        />
                      }
                      label={
                        <Typography component="span" variant="body2">
                          {memberLabel(c)}
                        </Typography>
                      }
                    />
                    {zygositySelect(
                      c.pid,
                      t('Zygosity with {{member}}', { member: memberLabel(c) })
                    )}
                  </>
                )
              )}
            </>
          )}

          {isMultiple && !candidates.length && !mergeCandidates.length && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
            >
              {t('No other full sibling is available to add to this set.')}
            </Typography>
          )}

          {incomplete && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
            >
              {t(
                'This set is not fully recorded — saving will link every member to every other.'
              )}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              disabled={isDisabled || !canSave}
              onClick={save}
            >
              {t('Save')}
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>
              {t('Cancel')}
            </Button>
            {isMultiple && (
              <Button
                size="small"
                color="error"
                disabled={isDisabled}
                onClick={clear}
                sx={{ ml: 'auto' }}
              >
                {notAMultipleLabel(setSize, t)}
              </Button>
            )}
          </Box>

          {/* Leaving a set of three does not dissolve it — the others stay a pair — so
              say what survives before the click rather than after. */}
          {isMultiple && remainderConsequence(setSize, t) && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
            >
              {remainderConsequence(setSize, t)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
